import React, { useState, useEffect } from "react";
import {
  Badge,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Popover,
  Tooltip,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import tifologo from "../../assets/TifoXR_CMS.jpeg";
import { NAVIGATION } from "./NavigationDetails";
import { useLocalText } from "../common/useLocalText";
import { usePermissions } from "../../hooks/usePermissions";
import { useApproval } from "../../context/ApprovalContext";
import { useSpaceConfig } from "../../context/SpaceConfigContext";
import { useNavigationGuard } from "../../context/NavigationGuardContext";

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 64;

const isSegmentActive = (pathname, segment) =>
  segment === "/"
    ? pathname === "/"
    : pathname === segment || pathname.startsWith(segment + "/");

// `nested` is a child of a group: indented, no icon, slightly smaller. `onNavigate` runs after a click that is
// allowed to navigate (it closes the mobile drawer or the collapsed-sidebar flyout).
function NavItem({ item, collapsed, onNavigate, badgeCount = 0, nested = false }) {
  const title = useLocalText(item.title);
  const { pathname } = useLocation();
  const { requestNavigation } = useNavigationGuard();
  const isActive = isSegmentActive(pathname, item.segment);

  const iconEl = badgeCount > 0
    ? <Badge badgeContent={badgeCount} color="error" max={99}>{item.icon}</Badge>
    : item.icon;

  const handleClick = (e) => {
    // A page with unsaved local edits (e.g. AdminPortal's Feature Flags tab) can register
    // a guard that intercepts this navigation instead of silently discarding the draft.
    if (!requestNavigation(item.segment)) { e.preventDefault(); return; }
    onNavigate?.();
  };

  return (
    <Tooltip title={collapsed ? title : ""} placement="right" arrow>
      <ListItemButton
        component={Link}
        to={item.segment}
        onClick={handleClick}
        aria-current={isActive ? "page" : undefined}
        sx={{
          justifyContent: collapsed ? "center" : "flex-start",
          paddingLeft: collapsed ? 0 : (nested ? 6.5 : 2),
          py: nested ? 0.75 : undefined,
          color: "white",
          borderLeft: collapsed ? "none" : (isActive ? "4px solid #5383b3" : "4px solid transparent"),
          bgcolor: isActive ? "rgba(255,255,255,0.1)" : "transparent",
          transition: "all 0.15s ease",
          "&:hover": {
            bgcolor: isActive ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.05)",
          },
        }}
      >
        {!nested && (
          <ListItemIcon
            sx={{
              color: "white",
              minWidth: collapsed ? "unset" : "40px",
              justifyContent: "center",
              opacity: isActive ? 1 : 0.75,
            }}
          >
            {iconEl}
          </ListItemIcon>
        )}
        {!collapsed && (
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              fontSize: nested ? 13.5 : 14,
              fontWeight: isActive ? 700 : 400,
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
}

// A collapsible group of pages (e.g. "Games"). Expanded sidebar: a header that opens and closes its children,
// open by itself when the current route is one of them. Icon-only sidebar: the header opens a small flyout beside
// it with the same links. The mobile drawer always renders the expanded form (`collapsed` is false there).
function NavGroup({ item, collapsed, onNavigate }) {
  const title = useLocalText(item.title);
  const { pathname } = useLocation();
  const childActive = item.children.some((child) => isSegmentActive(pathname, child.segment));
  const [open, setOpen] = useState(childActive);
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const panelId = `nav-group-${item.key}`;

  // Landing on one of the group's pages (link, bookmark, back button) opens it; it never closes by itself.
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  // The flyout belongs to the icon-only form; expanding the sidebar must not leave it armed to reappear.
  useEffect(() => {
    if (!collapsed) setFlyoutAnchor(null);
  }, [collapsed]);

  const closeFlyout = () => setFlyoutAnchor(null);
  const handleChildNavigate = () => { closeFlyout(); onNavigate?.(); };

  // A closed (or icon-only) group still shows where the user is.
  const headerActive = childActive && (collapsed || !open);

  const handleHeaderClick = (e) => {
    if (collapsed) setFlyoutAnchor(e.currentTarget);
    else setOpen((prev) => !prev);
  };

  return (
    <>
      <Tooltip title={collapsed ? title : ""} placement="right" arrow>
        <ListItemButton
          onClick={handleHeaderClick}
          aria-expanded={collapsed ? Boolean(flyoutAnchor) : open}
          aria-controls={collapsed ? undefined : panelId}
          aria-haspopup={collapsed ? "menu" : undefined}
          sx={{
            justifyContent: collapsed ? "center" : "flex-start",
            paddingLeft: collapsed ? 0 : 2,
            color: "white",
            borderLeft: collapsed ? "none" : (headerActive ? "4px solid #5383b3" : "4px solid transparent"),
            bgcolor: headerActive ? "rgba(255,255,255,0.1)" : "transparent",
            transition: "all 0.15s ease",
            "&:hover": { bgcolor: headerActive ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.05)" },
          }}
        >
          <ListItemIcon
            sx={{
              color: "white",
              minWidth: collapsed ? "unset" : "40px",
              justifyContent: "center",
              opacity: childActive ? 1 : 0.75,
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!collapsed && (
            <>
              <ListItemText
                primary={title}
                primaryTypographyProps={{ fontSize: 14, fontWeight: childActive ? 700 : 400 }}
              />
              {open
                ? <ExpandLessIcon sx={{ fontSize: 20, opacity: 0.75 }} />
                : <ExpandMoreIcon sx={{ fontSize: 20, opacity: 0.75 }} />}
            </>
          )}
        </ListItemButton>
      </Tooltip>

      {!collapsed && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List id={panelId} component="div" disablePadding aria-label={title}>
            {item.children.map((child) => (
              <NavItem key={child.segment} item={child} collapsed={false} nested onNavigate={onNavigate} />
            ))}
          </List>
        </Collapse>
      )}

      {collapsed && (
        <Popover
          open={Boolean(flyoutAnchor)}
          anchorEl={flyoutAnchor}
          onClose={closeFlyout}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                bgcolor: "#0a326d",
                color: "white",
                minWidth: 190,
                ml: 0.5,
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.12)",
              },
            },
          }}
        >
          <List component="div" disablePadding aria-label={title} sx={{ py: 0.5 }}>
            <Box sx={{ px: 2, py: 0.75, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.6 }}>
              {title}
            </Box>
            {item.children.map((child) => (
              <NavItem key={child.segment} item={child} collapsed={false} nested onNavigate={handleChildNavigate} />
            ))}
          </List>
        </Popover>
      )}
    </>
  );
}

const Sidebar = ({ onWidthChange, isMobile = false, mobileOpen = false, onClose }) => {
  const { canAccessNav } = usePermissions();
  const { inboxCount } = useApproval();
  const { config: spaceConfig, loading: configLoading } = useSpaceConfig() ?? {};
  const isVisible = (item) => {
    if (!canAccessNav(item.segment)) return false;
    if (!configLoading && item.flag && spaceConfig && spaceConfig[item.flag] === false) return false;
    return true;
  };
  // A group shows only the children the user may open, and not at all when none of them is allowed.
  const visibleNav = NAVIGATION
    .map((item) => (item.children ? { ...item, children: item.children.filter(isVisible) } : item))
    .filter((item) => (item.children ? item.children.length > 0 : isVisible(item)));
  const [collapsed, setCollapsed] = useState(false);
  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  useEffect(() => {
    if (!isMobile) onWidthChange?.(width);
  }, [width, onWidthChange, isMobile]);

  const toggleCollapse = () => setCollapsed((prev) => !prev);

  const drawerPaperSx = {
    width: isMobile ? EXPANDED_WIDTH : width,
    transition: "width 0.2s ease",
    boxSizing: "border-box",
    background: "#0a326d",
    overflowX: "hidden",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: (!isMobile && collapsed) ? "center" : "flex-start",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255,255,255,0.25) rgba(255,255,255,0.05)",
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255,255,255,0.05)",
      borderRadius: "4px",
      margin: "8px 0",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255,255,255,0.25)",
      borderRadius: "6px",
      border: "1px solid rgba(255,255,255,0.1)",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "rgba(255,255,255,0.45)",
    },
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={isMobile ? onClose : undefined}
      ModalProps={isMobile ? { keepMounted: true } : undefined}
      sx={{
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: drawerPaperSx,
      }}
    >
      {/* Logo + toggle — separated from nav by a border */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: (!isMobile && collapsed) ? "center" : "space-between",
          padding: "10px 15px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {(isMobile || !collapsed) && (
          <Box
            component="img"
            src={tifologo}
            alt="TifoXR Logo"
            sx={{ height: 60, width: 60, objectFit: "contain" }}
          />
        )}
        {!isMobile && (
          <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
            <IconButton
              onClick={toggleCollapse}
              sx={{
                color: "white",
                marginLeft: collapsed ? 0 : "auto",
                transition: "transform 0.2s ease",
                transform: collapsed ? "rotate(180deg)" : "none",
              }}
            >
              {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <List sx={{ flexGrow: 1, width: "100%", pt: 1 }}>
        {visibleNav.map((item) => (item.children ? (
          <NavGroup
            key={item.key}
            item={item}
            collapsed={!isMobile && collapsed}
            onNavigate={isMobile ? onClose : undefined}
          />
        ) : (
          <NavItem
            key={item.segment}
            item={item}
            collapsed={!isMobile && collapsed}
            onNavigate={isMobile ? onClose : undefined}
            badgeCount={item.hasBadge ? inboxCount : 0}
          />
        )))}
      </List>
    </Drawer>
  );
};

export default Sidebar;
